import React, { useState, useEffect } from "react";
import theme from "../../theme/theme";
import { Api } from "../../network/api";
import { useNavigate } from "react-router-dom";
import { PaperIcon } from "../../assets/icons";

const { Colors } = theme;

const Payment = () => {
  const navigate = useNavigate();
  const [activePackage, setActivePackage] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadActivePackage();
    loadPaymentHistory();
  }, []);

  const loadActivePackage = async () => {
    try {
      setLoading(true);
      const response = await Api.getActivePackage();
      setActivePackage(response.data);
    } catch (error) {
      console.error("Error loading active package:", error);
      setActivePackage(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await Api.getPaymentHistory();
      setPaymentHistory(response.data || []);
    } catch (error) {
      console.error("Error loading payment history:", error);
      setPaymentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "completed":
      case "success":
        return { backgroundColor: "#dcfce7", color: "#166534" };
      case "pending":
        return { backgroundColor: "#fef3c7", color: "#92400e" };
      case "expired":
      case "cancelled":
      case "failed":
        return { backgroundColor: "#fee2e2", color: "#991b1b" };
      default:
        return { backgroundColor: "#f3f4f6", color: "#374151" };
    }
  };

  const getPackageTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "one-time":
      case "onetime":
        return "One-Time Payment";
      case "monthly":
        return "Monthly Subscription";
      case "annually":
        return "Annual Subscription";
      default:
        return type || "Unknown";
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Active Package</h2>
          <p className="text-gray-600 mt-1">
            Your current subscription and package details
          </p>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-2 text-gray-600">Loading package...</span>
            </div>
          ) : activePackage ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Package Type
                  </label>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {getPackageTypeLabel(activePackage.package_type)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <div className="mt-1">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                      style={getStatusStyle(activePackage.status)}
                    >
                      {activePackage.status || "Active"}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Start Date
                  </label>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatDate(activePackage.start_date)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    {activePackage.package_type?.toLowerCase() === "monthly" ||
                    activePackage.package_type?.toLowerCase() === "annually"
                      ? "Next Billing Date"
                      : "Expiry Date"}
                  </label>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatDate(
                      activePackage.end_date ||
                        activePackage.next_billing_date ||
                        activePackage.expiry_date
                    )}
                  </p>
                </div>
                {activePackage.price && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Price
                    </label>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {formatCurrency(activePackage.price)}
                    </p>
                  </div>
                )}
              </div>
              {(!activePackage.status ||
                activePackage.status?.toLowerCase() !== "active") && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => navigate("/payment")}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 hover:opacity-90"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                    }}
                  >
                    Renew or Upgrade Package
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <PaperIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No active package
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                You don't have an active subscription. Choose a plan to get
                started.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate("/payment")}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 hover:opacity-90"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                  }}
                >
                  View Plans
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Payment History</h2>
          <p className="text-gray-600 mt-1">
            View all your past transactions and payments
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction ID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingHistory ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                      <span className="ml-2 text-gray-600">
                        Loading payment history...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : paymentHistory.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    {/* <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg> */}
                    <PaperIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No payment history
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Your payment transactions will appear here.
                    </p>
                  </td>
                </tr>
              ) : (
                paymentHistory.map((payment) => (
                  <tr
                    key={payment.id || payment.transaction_id}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.payment_date || payment.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getPackageTypeLabel(
                          payment.package_type || payment.plan_type
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(payment.amount || payment.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={getStatusStyle(payment.status)}
                      >
                        {payment.status || "Completed"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {payment.transaction_id || payment.id || "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Payment;
